import { ChangeEvent, useEffect, useState } from 'react'
import { FormHelperText, Grid, TextareaAutosize } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import * as Redux from 'redux'
import { AxiosPromise } from 'axios'
import { ICommonQuery } from '@Models/common.model'
import { MClassification } from '@Models/classification.model'
import { APIServices } from '@Services/api.service'
import { CNoneOption } from '@Constants/common.constant'
import './contact.scss'
import { FormService } from '@Services/form.service'
import MessagesCenter from '@Pages/Client/contact/MessagesCenter'
import ClassificationAction from '@Stores/client/actions/classification.action'
import ClientContactActions from '@Stores/client/actions/contact.action'
import { EClassifications } from '@Enum/classifications.enum'
import BaseSelect from '@Components/Form/BaseSelect/BaseSelect'
import { RedditTextField } from '@Components/Form/RedditTextField/RedditTextField'
import PageHeader from '@Components/Pages/PageHeader/PageHeader'
import BaseButton from '@Components/Form/BaseButton/BaseButton'

// dummy data
const defaultValues = {
    category: '',
    topic: '',
    title: '',
    description: '',
}

interface IContact {
    // redux
    addContact?: (body: any) => AxiosPromise
    getContactCategories?: (params?: ICommonQuery) => void
    getContactTopics?: (params?: ICommonQuery) => void
    contactCategories?: MClassification[]
    contactTopics?: MClassification[]
}

function Contact(props: IContact) {
    // core section
    const {
        formState: { errors },
        reset,
        handleSubmit,
        control,
        setValue,
    } = useForm({ defaultValues })
    const {
        addContact,
        getContactCategories,
        getContactTopics,
        contactCategories,
        contactTopics,
    } = props

    // states section
    const [disabled, setDisabled] = useState(false)

    // hooks section
    useEffect(() => {
        if (getContactCategories) getContactCategories()
        // if (getContactTopics) getContactTopics()
        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    // functions section
    /**
     * handle form submit and create contact
     * @param formData inputs value
     */
    const onSubmit = (formData: any) => {
        setDisabled(true)
        if (addContact)
            addContact(formData)
                .then(() => {
                    setDisabled(false)
                    reset(defaultValues)
                })
                .catch(() => setDisabled(false))
    }

    /**
     * handle ok click and submit form
     */
    const handleClickOK = () => {
        handleSubmit(onSubmit)()
    }

    // main
    return (
        <div className="page-contact">
            <PageHeader title="Contact" />
            <Grid container spacing={2}>
                <Grid item sm={4} xs={12}>
                    <div className="h-100">
                        <Controller
                            control={control}
                            name="category"
                            defaultValue=""
                            rules={{ required: true }}
                            render={({
                                field: { ref, value, onChange, ...field },
                            }: any) => (
                                <BaseSelect
                                    value={value}
                                    onChange={(
                                        evt: ChangeEvent<HTMLInputElement>
                                    ) => {
                                        onChange(evt)
                                        if (getContactTopics)
                                            getContactTopics({
                                                parentCode: evt?.target?.value,
                                            })
                                        setValue('topic', '')
                                    }}
                                    options={
                                        contactCategories?.length
                                            ? [
                                                  CNoneOption,
                                                  ...contactCategories,
                                              ]
                                            : [CNoneOption]
                                    }
                                    inputRef={ref}
                                    className="mb-2"
                                    label="Category"
                                    fullWidth
                                    error={errors?.category && true}
                                    helperText={FormService.getErrorMessage(
                                        errors,
                                        'category',
                                        'Category'
                                    )}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="topic"
                            defaultValue=""
                            rules={{ required: true }}
                            render={({
                                field: { ref, value, onChange, ...field },
                            }: any) => (
                                <BaseSelect
                                    value={value}
                                    onChange={onChange}
                                    options={
                                        contactTopics?.length
                                            ? [CNoneOption, ...contactTopics]
                                            : [CNoneOption]
                                    }
                                    inputRef={ref}
                                    className="mb-2"
                                    label="Question Topic"
                                    fullWidth
                                    error={errors?.topic && true}
                                    helperText={FormService.getErrorMessage(
                                        errors,
                                        'topic',
                                        'Question Topic'
                                    )}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="title"
                            defaultValue=""
                            rules={{ required: true }}
                            render={({
                                field: { ref, value, onChange, ...field },
                            }: any) => (
                                <RedditTextField
                                    inputRef={ref}
                                    className="reddit-input mb-2 w-100"
                                    variant="filled"
                                    label="Title"
                                    value={value}
                                    onChange={onChange}
                                    error={errors?.title && true}
                                    helperText={FormService.getErrorMessage(
                                        errors,
                                        'title',
                                        'Title'
                                    )}
                                    disabled={disabled}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="description"
                            defaultValue=""
                            rules={{ required: true }}
                            render={({
                                field: { ref, value, onChange, ...field },
                            }: any) => (
                                <span>
                                    <TextareaAutosize
                                        ref={ref}
                                        value={value}
                                        onChange={onChange}
                                        className="text-area"
                                        placeholder="Enter Description"
                                        disabled={disabled}
                                    />
                                    {errors?.description && true && (
                                        <FormHelperText error className="ml-3">
                                            {FormService.getErrorMessage(
                                                errors,
                                                'description',
                                                'Description'
                                            )}
                                        </FormHelperText>
                                    )}
                                </span>
                            )}
                        />
                    </div>
                </Grid>
                <Grid item sm={8} xs={12}>
                    <MessagesCenter />
                </Grid>
            </Grid>
            <div className="text-right mt-3">
                <BaseButton
                    className="variant-red btn-lg-width"
                    onClick={handleClickOK}
                    disabled={disabled}
                    isLoading={disabled}
                >
                    Submit
                </BaseButton>
            </div>
        </div>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { contactCategories, contactTopics } = store?.client?.classifications
    return { contactCategories, contactTopics }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const { addContact } = ClientContactActions
    const { getClassifications } = ClassificationAction
    return {
        addContact: (body: any) => dispatch(addContact(body) as any),
        getContactCategories: (params?: ICommonQuery) =>
            dispatch(
                getClassifications(
                    EClassifications.CONTACT_CATEGORY,
                    params
                ) as any
            ),
        getContactTopics: (params?: ICommonQuery) =>
            dispatch(
                getClassifications(
                    EClassifications.CONTACT_TOPIC,
                    params
                ) as any
            ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Contact)
